import { FlexCell, FlexRow } from "@/common/StyledComponents";
import { Chip, IconButton } from "@mui/material";
import {
    ClockCircle,
    TrashBinMinimalistic,
    CheckCircle,
    Dislike,
    Like,
    Restart,
    Pen2,
} from "@solar-icons/react/ssr";
import {
    PURCHASE_ORDER_STATUS_APPROVED,
    PURCHASE_ORDER_STATUS_PENDING,
    PURCHASE_ORDER_STATUS_REJECTED,
    getVendorStatusColor,
    getPurchaseOrderStatusColor,
} from "@/vendor/constants";
import { request } from "@/Api";
import { useParams } from "react-router-dom";
import { formattedCurrency } from "@/utils";
import { useSelector } from "react-redux";
import useEntitlements from "@/auth/useEntitlements";
import {
    selectEditingPurchaseOrder,
    setEditingPurchaseOrder,
} from "@/actuals/actualSlice";
import { useDispatch } from "react-redux";

export default function PurchaseOrder({
    variant,
    purchaseOrder,
    onDelete,
    onUpdate,
    onInvite,
}) {
    const { projectId } = useParams();
    const dispatch = useDispatch();
    const { hasPermission } = useEntitlements();
    const canApprove = hasPermission("approve_purchase_orders");
    const projectVariant = variant == "project";
    const vendor = purchaseOrder.vendor;
    const handleInvitationReminderButton = () => {
        request
            .post(`purchase-orders`, {
                existing_vendors: [
                    { project_id: projectId, vendor_id: vendor.id },
                ],
                new_vendors: [],
            })
            .then((_) => {
                onInvite && onInvite(purchaseOrder);
            });
    };
    const handleRemove = () => {
        request.delete(`purchase-orders/${purchaseOrder.id}`).then((_) => {
            onDelete && onDelete(purchaseOrder);
        });
    };
    const handleStatusChange = (status) => () => {
        request
            .patch(`purchase-orders/${purchaseOrder.id}`, { status: status })
            .then((response) => {
                onUpdate && onUpdate(response.data);
            });
    };
    const handleEditButton = () => {
        dispatch(setEditingPurchaseOrder(purchaseOrder));
    };
    return (
        <FlexRow style={{ paddingBottom: 10, paddingTop: 10 }}>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={150}
            >
                {vendor.email}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={120}
            >
                {vendor.name}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                <VendorStatusChip vendor={vendor} />
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                {purchaseOrder.project_accounting_bills_submitted_count}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
                style={{
                    color:
                        purchaseOrder.project_accounting_bills_submitted_amount >
                            purchaseOrder.amount && "red",
                }}
            >
                {formattedCurrency(
                    purchaseOrder.project_accounting_bills_submitted_amount,
                    vendor.payment_currency,
                )}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                {formattedCurrency(
                    purchaseOrder.amount,
                    vendor.payment_currency,
                )}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                {purchaseOrder.number}
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                <PurchaseOrderStatusChip purchaseOrder={purchaseOrder} />
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                align="right"
                item
                minWidth={80}
            >
                {purchaseOrder.status == PURCHASE_ORDER_STATUS_PENDING &&
                    canApprove && (
                        <>
                            <IconButton
                                size="small"
                                onClick={handleStatusChange(
                                    PURCHASE_ORDER_STATUS_REJECTED,
                                )}
                            >
                                <Dislike />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleStatusChange(
                                    PURCHASE_ORDER_STATUS_APPROVED,
                                )}
                            >
                                <Like />
                            </IconButton>
                        </>
                    )}
                {purchaseOrder.status == PURCHASE_ORDER_STATUS_REJECTED && (
                    <>
                        <IconButton
                            size="small"
                            onClick={handleStatusChange(
                                PURCHASE_ORDER_STATUS_PENDING,
                            )}
                        >
                            <Restart />
                        </IconButton>
                    </>
                )}
                <IconButton size="small" onClick={handleEditButton}>
                    <Pen2 />
                </IconButton>
                {purchaseOrder._reminded ? (
                    <IconButton size="small" disabled>
                        <CheckCircle color="success" />
                    </IconButton>
                ) : (
                    <IconButton
                        size="small"
                        onClick={handleInvitationReminderButton}
                    >
                        <ClockCircle />
                    </IconButton>
                )}
                <IconButton size="small" onClick={handleRemove}>
                    <TrashBinMinimalistic />
                </IconButton>
            </FlexCell>
        </FlexRow>
    );
}

export function ProjectVendorHeader({ variant }) {
    const projectVariant = variant == "project";
    return (
        <FlexRow
            style={{ paddingBottom: 20, fontWeight: !projectVariant && 800 }}
        >
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={150}
            >
                Email
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={120}
            >
                Name
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                Status
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                Invoice Submitted
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                Total Billed
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                Total PO
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                PO #
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                item
                minWidth={80}
            >
                PO Status
            </FlexCell>
            <FlexCell
                fontSize={projectVariant ? "14px" : "12px"}
                align="right"
                item
                minWidth={80}
            >
                Actions
            </FlexCell>
        </FlexRow>
    );
}

export function VendorStatusChip({ vendor }) {
    return (
        <Chip
            label={vendor.status || "New"}
            color={getVendorStatusColor(vendor.status)}
        />
    );
}

export function PurchaseOrderStatusChip({ purchaseOrder }) {
    const status = purchaseOrder.status || "Pending";
    return <Chip label={status} color={getPurchaseOrderStatusColor(status)} />;
}
